import {
    createStyles,
    Dialog,
    Grid,
    makeStyles,
    Theme,
    Typography
} from "@material-ui/core"
import { push } from "connected-react-router";
import React, { useCallback } from "react"
import { connect, MapDispatchToPropsFunction, MapStateToProps, useSelector } from "react-redux"
import { IMeetingBookingOrder, IPropertyLink, WebshopType } from "shared/Types/appTypes"
import { closeCheckoutModal } from "../Actions/checkoutModalActions";
import { closeConfirmBookingModal } from "../Actions/confirmBookingModalActions";
import { closeMeetingBookedModal, ICloseMeetingBookedModal } from "shared/Modules/Meeting/meetingModalActions";
import { clearShoppingCart } from "../Actions/shoppingCartActions";
import { IStoreState } from '../Reducers/rootReducer';
import CalendarImportLinks from "./CalendarImportLinks";
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal";
import { Localized } from '@fluent/react';
import { useDateTime } from "shared/Modules/Localization/useDateTime"
import { clearEditOrder } from "shared/Modules/Meeting/meetingActions";
import { useAppNavigation } from "../../shared/hooks/useAppNavigation"
import { selectUserCanteenId } from "../../shared/Modules/User/userSelectors"
import Screen from 'shared/Components/Skeleton/Screen'
import { faCoffee, faSignOut } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import clsx from "clsx"
import { editMeetingOrder } from "../../shared/Modules/MeetingPlanning/Redux/EditOrder/MeetingPlanningEditOrderActions"
import { DeliveryType, IEditMeetingOrderData } from "../../shared/Types/MeetingPlanning/deliveryForm"
import { selectCateringModule } from "shared/Modules/Properties/propertySelectors"

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: theme.palette.common.white,
        },
        wrap: {
            padding: '65px 0',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '520px'
        },
        mr_2: {
            marginRight: theme.spacing(2),
        },
        mb_1: {
            marginBottom: theme.spacing(1),
        },
        mb_2: {
            marginBottom: theme.spacing(2),
        },
        mb_4: {
            marginBottom: theme.spacing(4),
        },
        bookingTitle: {
            fontSize: '24px',
            fontWeight: 700
        },
        bookingTimeTitle: {
            color: 'rgba(0, 0, 0, 0.6)',
            margin: '0 8px 0 0',
        },
        bookingTimeValue: {
            color: '#505050',
            fontWeight: 600,
        },
        calendarLinks: {
            marginBottom: '96px'
        },
        choiceTitle: {
            fontSize: '18px'
        },
        nextChoicesButton: {
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            marginBottom: '12px',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            background: 'unset',
            width: "100%"
        },
        nextChoicesButtonText: {
            fontWeight: 500,
        },
        nextChoicesCoffeeButtonIcon: {
            width: "52px",
            height: "52px",
            borderRadius: '8px',
            backgroundColor: "#CCFBF1",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        nextChoicesSignOutButtonIcon: {
            width: "52px",
            height: "52px",
            borderRadius: '8px',
            backgroundColor: "#FEE2E2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }
    }),
);

// These are the props that can/should be passed from a parent component
// It is the only exported prop interface since all other props are internal
export interface IOwnProps {
    // This component does not have own props
}

// These are the props we expect to be passed from Redux state
interface IStateProps {
    isDrawerOpen: boolean
    order: IMeetingBookingOrder | null
    links: IPropertyLink[] | null
    editOrder: IMeetingBookingOrder | null
    showCateringModule: boolean
}

// These are the props used to dispatch actions from the component
interface IDispatchProps {
    hideMeetingBookedModal: () => ICloseMeetingBookedModal,
    onCloseMeetingBookedModal: (order: IEditMeetingOrderData | null) => ICloseMeetingBookedModal,
}

type IProps =
    & IOwnProps
    & IStateProps
    & IDispatchProps

const mapStateToProps: MapStateToProps<IStateProps, IOwnProps, IStoreState> = ({ editOrder, meetingBookedModal, properties, shoppingCart }) => ({
    isDrawerOpen: meetingBookedModal.open,
    order: meetingBookedModal.order,
    links: meetingBookedModal.links,
    editOrder: editOrder.order,
    showCateringModule: selectCateringModule({ properties }),
});
const mapDispatchToProps: MapDispatchToPropsFunction<IDispatchProps, IOwnProps> = (dispatch) => ({
    hideMeetingBookedModal: () => {
        dispatch(clearShoppingCart())
        dispatch(closeCheckoutModal())
        dispatch(closeConfirmBookingModal())
        dispatch(clearEditOrder())
        dispatch(push('/'))
        return dispatch(closeMeetingBookedModal());
    },
    onCloseMeetingBookedModal: (order: IEditMeetingOrderData | null) => {
        dispatch(clearShoppingCart())
        dispatch(closeCheckoutModal())
        dispatch(closeConfirmBookingModal())
        dispatch(clearEditOrder())
        dispatch(editMeetingOrder(order))
        return dispatch(closeMeetingBookedModal());
    },
});

const ModalContent = (props: IProps) => {
    const classes = useStyles()
    const userCanteenId = useSelector(selectUserCanteenId)
    const { editOrder, order, hideMeetingBookedModal, onCloseMeetingBookedModal, links, showCateringModule } = props
    const dateTimeFactory = useDateTime()
    const { goToWebshop } = useAppNavigation()

    const goToMeetingPlanning = useCallback(
        () => {
            if (order?.meetingDetails && userCanteenId) {
                const { meetingName, startDateTime } = order.meetingDetails

                const orderDetails: IEditMeetingOrderData = {
                    orderId: order.id,
                    orderData: {
                        meetingName,
                        deliveryDate: startDateTime.split(' ')[0],
                        deliveryTime: dateTimeFactory.toIsoTime(dateTimeFactory.fromFNDateTime(startDateTime)),
                        deliveryType: DeliveryType.DELIVERY,
                        deliveryLocation: order?.bookingDetails?.resources?.find((resource) => resource.id)?.id?.toString() || '',
                    },
                    isResourceBookingOrder: true
                }

                goToWebshop(userCanteenId, WebshopType.MEETING_BOOKING)
                onCloseMeetingBookedModal(orderDetails)

                return
            }

            hideMeetingBookedModal()
        },
        [goToWebshop, userCanteenId]
    )

    if (order === null) {
        return (
            <div />
        );
    }

    return (
        <Screen
            name="statusView"
            fitPage
            fullHeight
            showPrimaryTopBar
            onBackButtonPress={hideMeetingBookedModal}
            backButtonIcon="cross"
        >
            <Grid container className={classes.wrap} direction="column" wrap="nowrap">
                <Grid container item alignItems="center" justifyContent="center" className={classes.mb_4}>
                    <Localized id={!editOrder ? "meetingBooked-complete" : "meetingUpdated-labels"}>
                        <Typography variant="h4" className={classes.bookingTitle}>{!editOrder ? "Booking gennemført" : "Møde opdateret"}</Typography>
                    </Localized>
                </Grid>
                <Grid container item alignItems="center" justifyContent="center" className={classes.mb_2}>
                    <Localized id="meetingBooked-when">
                        <Typography variant="body1" className={classes.bookingTimeTitle}>Hvornår:</Typography>
                    </Localized>
                    <Typography variant="body1" className={classes.bookingTimeValue}>
                        {order.meetingDetails ? dateTimeFactory.formatMeetingPeriod(order.meetingDetails) : ""}
                    </Typography>
                </Grid>
                <Grid container item alignItems="center" justifyContent="center" className={classes.calendarLinks}>
                    {links !== null ? <CalendarImportLinks links={links} /> : undefined}
                </Grid>
                <Grid container item alignItems="center" justifyContent="center" className={classes.mb_2}>
                    <Localized id="meetingBooked-next-choice">
                        <Typography variant="h5" className={classes.choiceTitle}>Hvad vil du foretage dig nu?</Typography>
                    </Localized>
                </Grid>
                <Grid container item alignItems="center" justifyContent="center">
                    {showCateringModule ? <button className={classes.nextChoicesButton} onClick={goToMeetingPlanning}>
                        <span className={clsx(classes.nextChoicesCoffeeButtonIcon, classes.mr_2)}>
                            <FontAwesomeIcon icon={faCoffee} size="lg" color="#14B8A6" />
                        </span>
                        <Localized id="meetingBooked-next-choice-go-to-meeting-planning">
                            <Typography variant="body1" className={classes.nextChoicesButtonText}>Tilføj mødeforplejning</Typography>
                        </Localized>
                    </button> : null}
                    <button className={classes.nextChoicesButton} onClick={hideMeetingBookedModal}>
                        <span className={clsx(classes.nextChoicesSignOutButtonIcon, classes.mr_2)}>
                            <FontAwesomeIcon icon={faSignOut} size="lg" color="#EF4444" />
                        </span>
                        <Localized id="meetingBooked-next-choice-finish">
                            <Typography variant="body1" className={classes.nextChoicesButtonText}>Jeg er færdig</Typography>
                        </Localized>
                    </button>
                </Grid>
            </Grid>
        </Screen>
    )
}

const Modal = (props: IProps) => {
    const { isDrawerOpen, hideMeetingBookedModal } = props;

    return (
        <div>
            <Dialog fullScreen open={isDrawerOpen}>
                <ErrorHandlerModal close={hideMeetingBookedModal}>
                    <ModalContent {...props} />
                </ErrorHandlerModal>
            </Dialog>
        </div>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
